import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { WEATHER_PAGE_SIZE } from 'ots-constants';
import { IRegionResortsPaginated, IResortShortReport } from 'api/region/types';
import { IPaginationResponse, OpenFlag, ResortStatus } from 'api/resort/types';
import { fetchRegionResortsByStatus } from 'api/region/static';
import { useVisibilityWindow } from 'util/useVisibilityWindow';
import { Diagnostics } from 'util/diagnostics';
import { createProjectedClosingGridColumns } from 'components/region/WeatherReports/ProjectedClosingGridColumns';
import { createRegionWeatherGridColumns } from 'components/region/WeatherReports/RegionWeatherGridColumns';
import { HeaderClickEvent, IGridColumn, IGridSorting } from 'components/common/Grid/types';
import { DisplayType } from 'components/region/WeatherRow/types';
import Grid from 'components/common/Grid/Grid';
import { getLocale } from 'i18n/locale';
import styles from './styles.module.scss';

export interface IProps {
  resorts: IRegionResortsPaginated;
  openFlag: OpenFlag | ResortStatus.openResorts | ResortStatus.projectedClosing;
  regionId: string;
  isFresh: boolean;
}

export const WeatherReports = ({
  resorts, openFlag, isFresh, regionId,
}: IProps) => {
  const { t } = useOTSTranslation('common');
  const [resortsList, setResortsList] = useState<Array<IResortShortReport>>(resorts.data);
  const [pagination, setPagination] = useState<IPaginationResponse>(resorts.pagination);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadMore, setLoadMore] = useState<boolean>(resorts.pagination.count > resorts.pagination.limit);
  const [gridSorting, setGridSorting] = useState<IGridSorting>();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Render the primary region only if this region includes resorts that span more than one primary region. For example "Epic Pass" or "Icon Pass".
  // Note for now the region title rendered is `region_en` so only enabling for en-US for now.
  const showRegionTitle = new Set(resorts.data.map((r) => r.region_id)).size > 1 && getLocale() === 'en-US';

  const isWeather = openFlag !== ResortStatus.projectedClosing;
  const columns = isWeather ? createRegionWeatherGridColumns(openFlag as DisplayType, true, showRegionTitle) : createProjectedClosingGridColumns();
  const buttonVisible = useVisibilityWindow(buttonRef, false, true, 0);

  const loadNextPage = useCallback(() => {
    setIsLoading(true);
    const nextPage = pagination.page + 1;

    fetchRegionResortsByStatus(
      regionId,
      openFlag,
      nextPage,
      WEATHER_PAGE_SIZE,
      gridSorting?.orderBy,
      gridSorting?.direction,
    ).then((data) => {
      const updatedResortList = [...resortsList, ...data.data];
      setResortsList(updatedResortList);
      setPagination(data.pagination);

      setLoadMore(data.pagination.count > updatedResortList.length);
      setIsLoading(false);
    }).catch((e) => {
      Diagnostics.error({
        tag: 'region-resorts',
        error: e,
        message: `Failed to load next page for region ${regionId}`,
        data: { regionId, nextPage, openFlag },
      });
      setIsLoading(false);
    });
  }, [resortsList, openFlag, pagination, gridSorting]);

  const onSortingChange = useCallback(async (event: HeaderClickEvent, column: IGridColumn, sorting?: IGridSorting) => {
    setIsLoading(true);
    try {
      const response = await fetchRegionResortsByStatus(
        regionId,
        openFlag,
        0,
        WEATHER_PAGE_SIZE,
        sorting?.orderBy,
        sorting?.direction,
      );
      setResortsList(response.data);
      setPagination(response.pagination);
      setLoadMore(response.pagination.count > response.data.length);
      setGridSorting(sorting);
    } catch (e: any) {
      Diagnostics.error({
        tag: 'region-resorts',
        error: e,
        message: `Failed to load next page for region ${regionId}`,
        data: { regionId, nextPage: pagination.page, openFlag },
      });
    }
    setIsLoading(false);
  }, [regionId, openFlag, pagination]);

  const loadMoreAutomatically = useCallback(() => {
    if (buttonVisible && !isLoading) {
      if (buttonRef.current) {
        const pos = buttonRef.current.getBoundingClientRect();
        const topEdgeIn = pos.y > 0 && pos.y < window.innerHeight;
        const bottomEdgeIn = pos.y + pos.height > 0 && pos.y + pos.height < window.innerHeight;
        const isVisible = topEdgeIn || bottomEdgeIn;

        if (isVisible) {
          setIsLoading(true);
          setTimeout(() => {
            loadNextPage();
          }, 10);
        }
      }
    }
  }, [isLoading, buttonVisible, loadNextPage, setIsLoading]);

  useEffect(() => {
    setResortsList(resorts.data);
    setPagination(resorts.pagination);
    setLoadMore(resorts.pagination.count > resorts.pagination.limit);
  }, [resorts]);

  useEffect(() => {
    loadMoreAutomatically();
  }, [isLoading, buttonVisible, loadNextPage, setIsLoading]);

  const bluring = !isFresh || isLoading;

  return (
    <>
      <section className={classNames(styles.box)}>
        <Grid
          onSortingChange={onSortingChange}
          data={resortsList}
          total={pagination.count}
          columns={columns}
          isLoading={bluring}
          noValueSymbol={t('common.dash')}
          className={styles.collabsibleTable}
        />
      </section>
      {!isLoading && hasLoadMore && (
        <button
          ref={buttonRef}
          type="button"
          className={styles.loadMore}
          onClick={loadNextPage}
        >
          {t('common.loadMore')}
        </button>
      )}
    </>
  );
};
